import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentPortal({ user, config }) {
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [linkCode, setLinkCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAddChild, setShowAddChild] = useState(false);

  useEffect(() => {
    fetchLinkedChildren();
  }, []);

  const fetchLinkedChildren = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/parent/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data.children || []);
    } catch (err) {
      console.error("Error fetching children:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildProgress = async (childId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/parent/child/${childId}/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildProgress(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Error loading child progress");
    }
  };

  const handleLinkChild = async (e) => {
    e.preventDefault();
    if (!linkCode.trim()) {
      setError("Please enter a link code");
      return;
    }

    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/parent/link-child`,
        { link_code: linkCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(`Successfully linked to ${response.data.child_username}!`);
      setLinkCode("");
      setShowAddChild(false);
      fetchLinkedChildren();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired link code");
    }
  };

  const handleUnlinkChild = async (childId) => {
    if (!window.confirm("Are you sure you want to unlink this child?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/parent/unlink-child/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Child unlinked successfully");
      if (selectedChild?.id === childId) {
        setSelectedChild(null);
        setChildProgress(null);
      }
      fetchLinkedChildren();
    } catch (err) {
      setError(err.response?.data?.detail || "Error unlinking child");
    }
  };

  const selectChild = (child) => {
    setSelectedChild(child);
    fetchChildProgress(child.id);
  };

  if (loading) {
    return (
      <div className="card">
        <h3>👨‍👩‍👧‍👦 Parent Portal</h3>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="parent-portal">
      {/* Header */}
      <div className="card" style={{
        background: "linear-gradient(135deg, #1a2a4a 0%, #2a1a4a 100%)",
        borderTop: "4px solid #87CEEB"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          <div>
            <h2 style={{ color: "#87CEEB", margin: "0 0 5px 0" }}>
              👨‍👩‍👧‍👦 Parent Portal
            </h2>
            <p style={{ color: "#aaa", margin: 0 }}>
              Monitor your children's K-5 learning progress
            </p>
          </div>
          <button
            onClick={() => setShowAddChild(!showAddChild)}
            data-testid="add-child-btn"
            style={{
              background: "#90EE90",
              color: "#000",
              marginTop: 0
            }}
          >
            + Link Child Account
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success" style={{ background: "#1a4d1a", color: "#90EE90", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>{success}</div>}

      {/* Add Child Form */}
      {showAddChild && (
        <div className="card" style={{ background: "#1a2a3a", borderLeft: "4px solid #87CEEB" }}>
          <h4 style={{ color: "#87CEEB", margin: "0 0 15px 0" }}>
            Link Child Account
          </h4>
          <p style={{ color: "#aaa", marginBottom: "15px", fontSize: "0.9rem" }}>
            Enter the link code generated from your child's account settings.
          </p>
          <form onSubmit={handleLinkChild} style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <input
              type="text"
              value={linkCode}
              onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
              placeholder="Enter 8-character link code"
              maxLength={8}
              style={{
                flex: 1,
                minWidth: "200px",
                padding: "12px 15px",
                textTransform: "uppercase",
                letterSpacing: "2px",
                fontFamily: "monospace",
                fontSize: "1.1rem"
              }}
              data-testid="link-code-input"
            />
            <button type="submit" style={{ marginTop: 0 }} data-testid="link-child-submit">
              Link Account
            </button>
            <button 
              type="button" 
              onClick={() => setShowAddChild(false)}
              style={{ background: "#333", marginTop: 0 }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Children List */}
      {children.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>👧</div>
          <h3 style={{ color: "#FFD700", marginBottom: "15px" }}>No Linked Children</h3>
          <p style={{ color: "#aaa", marginBottom: "20px" }}>
            Link your child's account to monitor their learning progress.
          </p>
          <div style={{
            background: "#1a1a2a",
            padding: "20px",
            borderRadius: "8px",
            textAlign: "left"
          }}>
            <h4 style={{ color: "#87CEEB", marginBottom: "10px" }}>How to Link:</h4>
            <ol style={{ margin: 0, paddingLeft: "20px", color: "#aaa" }}>
              <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
                Your child logs into their account
              </li>
              <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
                Go to Settings → Generate Parent Link Code
              </li>
              <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
                Enter the 8-character code above
              </li>
              <li style={{ background: "transparent", border: "none", padding: "0" }}>
                You can now view their progress!
              </li>
            </ol>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
          {/* Children Sidebar */}
          <div style={{ flex: "0 0 280px", minWidth: "250px" }}>
            <div className="card">
              <h4 style={{ color: "#FFD700", margin: "0 0 15px 0" }}>
                Your Children ({children.length})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {children.map((child) => (
                  <div
                    key={child.id}
                    data-testid={`child-card-${child.id}`}
                    onClick={() => selectChild(child)}
                    style={{
                      background: selectedChild?.id === child.id ? "#2a3a4a" : "#1a1a1a",
                      padding: "15px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      borderLeft: `4px solid ${selectedChild?.id === child.id ? "#87CEEB" : "#333"}`,
                      transition: "all 0.2s"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <h5 style={{ color: "#FFD700", margin: "0 0 5px 0" }}>
                          {child.username}
                        </h5>
                        <p style={{ color: "#aaa", margin: 0, fontSize: "0.85rem" }}>
                          Grade Level: {child.grade_level || "K-5"}
                        </p>
                      </div>
                      <span style={{ fontSize: "1.5rem" }}>👤</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Child Progress Details */}
          <div style={{ flex: 1, minWidth: "300px" }}>
            {!selectedChild ? (
              <div className="card" style={{ textAlign: "center", padding: "40px" }}>
                <p style={{ color: "#aaa" }}>Select a child to view their progress</p>
              </div>
            ) : (
              <div>
                {/* Child Header */}
                <div className="card" style={{ background: "#1a2a3a" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
                    <div>
                      <h3 style={{ color: "#FFD700", margin: "0 0 5px 0" }}>
                        {selectedChild.username}'s Progress
                      </h3>
                      <p style={{ color: "#aaa", margin: 0, fontSize: "0.9rem" }}>
                        Grade: {selectedChild.grade_level || "K-5"} • 
                        Joined: {new Date(selectedChild.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleUnlinkChild(selectedChild.id)}
                      style={{ background: "#4d1a1a", marginTop: 0, fontSize: "0.85rem" }}
                    >
                      Unlink
                    </button>
                  </div>
                </div>

                {/* Progress Stats */}
                {childProgress && (
                  <>
                    <div className="card">
                      <h4 style={{ color: "#90EE90", margin: "0 0 15px 0" }}>
                        📊 Learning Statistics
                      </h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "15px" }}>
                        <div style={{ background: "#1a2a1a", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#90EE90" }}>
                            {childProgress.stats?.quizzes_taken || 0}
                          </div>
                          <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Quizzes Taken</div>
                        </div>
                        <div style={{ background: "#2a2a1a", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#FFD700" }}>
                            {childProgress.stats?.average_score || 0}%
                          </div>
                          <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Average Score</div>
                        </div>
                        <div style={{ background: "#1a2a3a", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#87CEEB" }}>
                            {childProgress.stats?.subjects_started || 0}
                          </div>
                          <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Subjects Started</div>
                        </div>
                        <div style={{ background: "#2a1a2a", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                          <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#DDA0DD" }}>
                            {childProgress.stats?.day_streak || 0}
                          </div>
                          <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Day Streak</div>
                        </div>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="card">
                      <h4 style={{ color: "#87CEEB", margin: "0 0 15px 0" }}>
                        📅 Recent Activity
                      </h4>
                      {childProgress.recent_activity?.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          {childProgress.recent_activity.map((activity, index) => (
                            <div
                              key={index}
                              style={{
                                background: "#1a1a1a",
                                padding: "12px 15px",
                                borderRadius: "8px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                              }}
                            >
                              <div>
                                <div style={{ color: "#FFD700", fontWeight: "bold" }}>
                                  {activity.subject_name}
                                </div>
                                <div style={{ color: "#aaa", fontSize: "0.85rem" }}>
                                  {activity.type} • {activity.grade_level}
                                </div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ 
                                  color: activity.score >= 70 ? "#90EE90" : "#FFD700",
                                  fontWeight: "bold"
                                }}>
                                  {activity.score}%
                                </div>
                                <div style={{ color: "#999", fontSize: "0.8rem" }}>
                                  {new Date(activity.date).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: "#aaa", textAlign: "center" }}>
                          No recent activity yet
                        </p>
                      )}
                    </div>

                    {/* Subject Progress */}
                    {childProgress.subject_progress?.length > 0 && (
                      <div className="card">
                        <h4 style={{ color: "#FFD700", margin: "0 0 15px 0" }}>
                          📚 Subject Progress
                        </h4>
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          {childProgress.subject_progress.map((subject, index) => (
                            <div key={index}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                                <span style={{ color: "#eee" }}>{subject.name}</span>
                                <span style={{ color: "#90EE90" }}>{subject.progress}%</span>
                              </div>
                              <div style={{
                                background: "#333",
                                borderRadius: "10px",
                                height: "8px",
                                overflow: "hidden"
                              }}>
                                <div style={{
                                  background: "linear-gradient(90deg, #90EE90 0%, #FFD700 100%)",
                                  height: "100%",
                                  width: `${subject.progress}%`,
                                  transition: "width 0.5s ease"
                                }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="card" style={{ background: "#1a1a2a", marginTop: "20px" }}>
        <h4 style={{ color: "#87CEEB", margin: "0 0 10px 0" }}>
          ℹ️ About Parent Portal
        </h4>
        <p style={{ color: "#aaa", margin: 0, fontSize: "0.9rem", lineHeight: "1.6" }}>
          The Parent Portal allows you to monitor your K-5 children's educational progress. 
          View quiz scores, track subject completion, and stay informed about their learning journey.
          Your child's privacy is protected - they control when to generate a link code.
        </p>
      </div>
    </div>
  );
}

export default ParentPortal;
